import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Card, CardContent, CardActions, Grid, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Chip, Skeleton, CircularProgress, Divider, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Map as MapIcon, Upload, Edit, Delete, CloudDownload, Explore, Stop, Save, Navigation, Warning, CheckCircle, Refresh, FileDownload, History, RestoreFromTrash } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppHooks';
import { fetchMyRobots } from '../../features/robots/robotsSlice';
import { fetchRobotMaps, uploadMap, deleteMap, saveMapToRobot, startSlam, stopSlam, saveSlamMap } from '../../features/maps/mapsSlice';
import { useWebSocket } from '../../hooks/useWebSocket';
import apiClient from '../../utils/apiClient';
import { addEventListener, removeEventListener, getConfig } from '../../utils/socketClient';

interface MapLoadError {
  robot_serial?: string;
  name: string;
  map_id: string;
  error: string;
  error_type: string;
  required_waypoints?: string[];
  current_waypoints?: string[];
}

const MapManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { robots } = useAppSelector((state) => state.robots);
  const { maps, isLoading, isSlamActive } = useAppSelector((state) => state.maps);
  const { robotsOnline, subscribeToRobot, getRobotTelemetry } = useWebSocket();
  const [selectedRobotId, setSelectedRobotId] = useState<string>(searchParams.get('robot') || '');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  // v6.3.36: zip upload support (single + bulk)
  const [uploadMode, setUploadMode] = useState<'files' | 'zip'>('zip');
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // v6.3.50: map version history
  const [versionsFor, setVersionsFor] = useState<any | null>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [versionsBusy, setVersionsBusy] = useState(false);
  const [slamDialogOpen, setSlamDialogOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ 
    name: '', 
    pgmFile: null as File | null, 
    yamlFile: null as File | null,
    keepoutPgmFile: null as File | null,
    keepoutYamlFile: null as File | null,
    waypointsFile: null as File | null
  });
  const [slamMapName, setSlamMapName] = useState('');
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  const [mapLoadError, setMapLoadError] = useState<MapLoadError | null>(null);
  const [mapLoading, setMapLoading] = useState<string | null>(null);
  const [loadedMapId, setLoadedMapId] = useState<string | null>(null);

  useEffect(() => { dispatch(fetchMyRobots()); }, [dispatch]);
  useEffect(() => { if (selectedRobotId) dispatch(fetchRobotMaps(selectedRobotId)); }, [selectedRobotId, dispatch]);

  const selectedRobot = robots.find((r) => r.id === selectedRobotId);
  
  // Ref to avoid stale closure for maps in event handler
  const mapsRef = React.useRef(maps);
  React.useEffect(() => { mapsRef.current = maps; }, [maps]);
  
  // Track current_map name for deferred matching
  const [currentMapName, setCurrentMapName] = useState<string | null>(null);
  
  // Get current map from robot config
  useEffect(() => {
    if (!selectedRobot) return;
    
    const handleRobotConfig = (data: any) => {
      if (data.robot_serial && data.robot_serial !== selectedRobot.serial_number) return;
      const cfg = data?.config;
      if (!cfg) return;
      
      const mapId = cfg.current_map_id;
      const mapName = cfg.current_map;
      console.log('MapManagement: robot_config, current_map:', mapName, 'id:', mapId, 'maps loaded:', mapsRef.current.length);
      
      if (mapId) {
        setLoadedMapId(mapId);
      }
      if (mapName) {
        setCurrentMapName(mapName);
        // Try immediate match using ref (not stale closure)
        const matched = mapsRef.current.find((m: any) => m.name === mapName);
        if (matched) {
          console.log('MapManagement: Matched by name:', mapName, '→', matched.id);
          setLoadedMapId(matched.id);
        }
      }
    };
    
    addEventListener('robot_config', handleRobotConfig);
    
    const isOnline = robotsOnline.includes(selectedRobot.serial_number);
    if (isOnline) {
      const timer = setTimeout(() => getConfig(selectedRobot.serial_number), 500);
      return () => { 
        clearTimeout(timer);
        removeEventListener('robot_config', handleRobotConfig); 
      };
    }
    
    return () => { removeEventListener('robot_config', handleRobotConfig); };
  }, [selectedRobot?.id, selectedRobot?.serial_number, robotsOnline]);
  
  // Deferred match: when maps load after robot_config arrived
  useEffect(() => {
    if (currentMapName && !loadedMapId && maps.length > 0) {
      const matched = maps.find(m => m.name === currentMapName);
      if (matched) {
        console.log('MapManagement: Deferred match:', currentMapName, '→', matched.id);
        setLoadedMapId(matched.id);
      }
    }
  }, [maps, currentMapName, loadedMapId]);
  
  // Subscribe to robot updates when selected
  useEffect(() => {
    if (selectedRobot) {
      subscribeToRobot(selectedRobot.serial_number);
    }
  }, [selectedRobot, subscribeToRobot]);
  
  // Listen for map loading events
  useEffect(() => {
    const handleMapLoading = (data: any) => {
      console.log('map_loading event received:', data);
      if (data.robot_serial === selectedRobot?.serial_number) {
        setMapLoading(data.status);
        if (data.status === 'validating') {
          setAlertMessage({ type: 'info', message: 'Validating map requirements...' });
        } else if (data.status === 'downloading') {
          setAlertMessage({ type: 'info', message: 'Downloading map files to robot...' });
        } else if (data.status === 'starting') {
          setAlertMessage({ type: 'info', message: 'Starting navigation...' });
        }
      }
    };
    
    const handleMapLoaded = (data: any) => {
      console.log('map_saved event received:', data);
      if (!data.robot_serial || data.robot_serial === selectedRobot?.serial_number) {
        setMapLoading(null);
        if (data.success && data.map_id) {
          setLoadedMapId(data.map_id);
          setAlertMessage({ type: 'success', message: `Map "${data.name}" saved to robot successfully!` });
        } else if (data.success && data.name) {
          const matched = maps.find(m => m.name === data.name);
          if (matched) setLoadedMapId(matched.id);
          setAlertMessage({ type: 'success', message: `Map "${data.name}" saved to robot!` });
        }
      }
    };
    
    const handleMapLoadError = (data: any) => {
      console.log('map_load_error event received:', data);
      // Accept if matches robot or if no robot_serial in data (backwards compat)
      if (!data.robot_serial || data.robot_serial === selectedRobot?.serial_number) {
        setMapLoading(null);
        setMapLoadError(data as MapLoadError);
        
        if (data.error_type === 'waypoints_missing') {
          setAlertMessage({ type: 'warning', message: 'Map requires waypoints. Please add them in the map editor.' });
        } else {
          setAlertMessage({ type: 'error', message: data.error });
        }
      }
    };
    
    const handleMapUploaded = (data: any) => {
      console.log('map_uploaded event received:', data);
      if (!data.robot_serial || data.robot_serial === selectedRobot?.serial_number) {
        setAlertMessage({ type: 'success', message: `Map "${data.map_name}" uploaded to cloud successfully!` });
        // Refetch maps to show the newly uploaded map
        if (selectedRobotId) {
          dispatch(fetchRobotMaps(selectedRobotId));
        }
      }
    };
    
    const handleSlamMapSaved = (data: any) => {
      console.log('slam_map_saved event received:', data);
      if (!data.robot_serial || data.robot_serial === selectedRobot?.serial_number) {
        if (data.success) {
          setAlertMessage({ type: 'success', message: `Map "${data.name}" saved${data.uploaded ? ' and uploaded' : ''}!` });
          // Refetch maps to show the newly saved map
          if (selectedRobotId) {
            dispatch(fetchRobotMaps(selectedRobotId));
          }
        }
      }
    };
    
    addEventListener('map_loading', handleMapLoading);
    addEventListener('map_loaded', handleMapLoaded);
    addEventListener('map_saved_to_robot', handleMapLoaded);  // Same handler
    addEventListener('map_load_error', handleMapLoadError);
    addEventListener('map_save_error', handleMapLoadError);   // Same handler
    addEventListener('map_uploaded', handleMapUploaded);
    addEventListener('slam_map_saved', handleSlamMapSaved);
    
    return () => {
      removeEventListener('map_loading', handleMapLoading);
      removeEventListener('map_loaded', handleMapLoaded);
      removeEventListener('map_saved_to_robot', handleMapLoaded);
      removeEventListener('map_load_error', handleMapLoadError);
      removeEventListener('map_save_error', handleMapLoadError);
      removeEventListener('map_uploaded', handleMapUploaded);
      removeEventListener('slam_map_saved', handleSlamMapSaved);
    };
  }, [selectedRobot, selectedRobotId, dispatch]);

  // Robot is online if in list OR if we have recent telemetry (within 10 seconds)
  const telemetry = selectedRobot ? getRobotTelemetry(selectedRobot.serial_number) : null;
  const isOnlineFromList = selectedRobot ? robotsOnline.includes(selectedRobot.serial_number) : false;
  const hasRecentTelemetry = telemetry && (Date.now() - telemetry.timestamp < 10000);
  const isRobotOnline = isOnlineFromList || !!hasRecentTelemetry;

  const handleUploadZip = async () => {
    if (!selectedRobotId || !uploadForm.name || !zipFile) return;
    try {
      const fd = new FormData();
      fd.append('name', uploadForm.name);
      fd.append('zip_file', zipFile);
      await apiClient.post(`/maps/robot/${selectedRobotId}/upload-zip`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadDialogOpen(false);
      setZipFile(null);
      setUploadForm({ name: '', pgmFile: null, yamlFile: null, keepoutPgmFile: null, keepoutYamlFile: null, waypointsFile: null });
      setAlertMessage({ type: 'success', message: 'Map uploaded from zip' });
      if (selectedRobotId) dispatch(fetchRobotMaps(selectedRobotId));
    } catch (e: any) {
      setAlertMessage({ type: 'error', message: e?.response?.data?.detail || 'Zip upload failed' });
    }
  };

  const openVersions = async (map: any) => {
    setVersionsFor(map);
    setVersionsBusy(true);
    setVersions([]);
    try {
      const r = await apiClient.get(`/maps/${map.id}/versions`);
      setVersions(r.data?.versions || []);
    } catch (e: any) {
      setAlertMessage({ type: 'error', message: e?.response?.data?.detail || 'Could not load versions' });
    } finally { setVersionsBusy(false); }
  };

  const restoreVersion = async (version: number) => {
    if (!versionsFor) return;
    if (!window.confirm(
      `Restore version ${version} of "${versionsFor.name}"?\n\n` +
      'The current files are archived first, so this can be undone. ' +
      'You must push the map to the robot afterwards to apply it there.'
    )) return;
    setVersionsBusy(true);
    try {
      const r = await apiClient.post(`/maps/${versionsFor.id}/versions/${version}/restore`);
      setAlertMessage({ type: 'success', message: r.data?.message || 'Version restored' });
      await openVersions(versionsFor);
      if (selectedRobotId) dispatch(fetchRobotMaps(selectedRobotId));
    } catch (e: any) {
      setAlertMessage({ type: 'error', message: e?.response?.data?.detail || 'Restore failed' });
    } finally { setVersionsBusy(false); }
  };

  const handleDownloadAll = async () => {
    if (!selectedRobotId) return;
    setBulkBusy(true);
    try {
      setAlertMessage({ type: 'info', message: 'Preparing archive…' });
      const res = await apiClient.get(`/maps/robot/${selectedRobotId}/download-all`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const serial = robots.find((r: any) => r.id === selectedRobotId)?.serial_number || 'robot';
      a.download = `${String(serial).replace(/[^a-zA-Z0-9-_]/g, '')}_all_maps.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setAlertMessage({ type: 'success', message: 'All maps downloaded' });
    } catch (e: any) {
      let msg = 'Download failed';
      // Error bodies come back as a Blob when responseType is blob
      try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch { /* keep default */ }
      setAlertMessage({ type: 'error', message: msg });
    } finally { setBulkBusy(false); }
  };

  const handleUploadBulk = async (file: File) => {
    if (!selectedRobotId || !file) return;
    setBulkBusy(true);
    try {
      setAlertMessage({ type: 'info', message: 'Importing maps…' });
      const fd = new FormData();
      fd.append('zip_file', file);
      const r = await apiClient.post(`/maps/robot/${selectedRobotId}/upload-bulk`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const failed = r.data?.failed || [];
      setAlertMessage({
        type: failed.length ? 'warning' : 'success',
        message: r.data?.message + (failed.length ? ` — failed: ${failed.map((f: any) => f.name).join(', ')}` : ''),
      });
      dispatch(fetchRobotMaps(selectedRobotId));
    } catch (e: any) {
      setAlertMessage({ type: 'error', message: e?.response?.data?.detail || 'Bulk import failed' });
    } finally { setBulkBusy(false); }
  };

  const handleUpload = async () => {
    if (!selectedRobotId || !uploadForm.name || !uploadForm.pgmFile || !uploadForm.yamlFile) return;
    try { 
      await dispatch(uploadMap({ 
        robotId: selectedRobotId, 
        name: uploadForm.name, 
        pgmFile: uploadForm.pgmFile, 
        yamlFile: uploadForm.yamlFile,
        keepoutPgmFile: uploadForm.keepoutPgmFile || undefined,
        keepoutYamlFile: uploadForm.keepoutYamlFile || undefined,
        waypointsFile: uploadForm.waypointsFile || undefined
      })).unwrap(); 
      setUploadDialogOpen(false); 
      setUploadForm({ name: '', pgmFile: null, yamlFile: null, keepoutPgmFile: null, keepoutYamlFile: null, waypointsFile: null }); 
      setAlertMessage({ type: 'success', message: 'Map uploaded successfully' }); 
    }
    catch (e: any) { 
      const errorMsg = typeof e === 'string' ? e : (e?.message || e?.detail || 'Upload failed');
      setAlertMessage({ type: 'error', message: errorMsg }); 
    }
  };

  const handleDelete = async (mapId: string) => {
    if (!window.confirm('Delete this map?')) return;
    try { 
      await dispatch(deleteMap(mapId)).unwrap(); 
      setAlertMessage({ type: 'success', message: 'Map deleted' }); 
    }
    catch (e: any) { 
      const errorMsg = typeof e === 'string' ? e : (e?.message || e?.detail || 'Delete failed');
      setAlertMessage({ type: 'error', message: errorMsg }); 
    }
  };

  const handleDownload = async (mapId: string, mapName: string) => {
    try {
      setAlertMessage({ type: 'info', message: 'Preparing download…' });
      const res = await apiClient.get(`/maps/${mapId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const safe = (mapName || 'map').replace(/[^a-zA-Z0-9-_]/g, '') || 'map';
      a.download = `${safe}_${mapId.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setAlertMessage({ type: 'success', message: 'Map downloaded' });
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Download failed';
      setAlertMessage({ type: 'error', message: msg });
    }
  };

  const handleSaveToRobot = async (mapId: string) => {
    if (!selectedRobotId) return;
    setMapLoading('saving');
    setAlertMessage({ type: 'info', message: 'Saving map to robot...' });
    try { 
      const result = await dispatch(saveMapToRobot({ robotId: selectedRobotId, mapId })).unwrap();
      // API returned success — files sent to robot
      setMapLoading(null);
      setAlertMessage({ type: 'success', message: 'Map saved to robot successfully!' });
    }
    catch (e: any) { 
      setMapLoading(null);
      const errorMsg = typeof e === 'string' ? e : (e?.message || 'Failed to save map to robot');
      setAlertMessage({ type: 'error', message: errorMsg });
    }
  };

  const handleStartSlam = async () => {
    if (!selectedRobotId) return;
    try { await dispatch(startSlam(selectedRobotId)).unwrap(); setAlertMessage({ type: 'success', message: 'SLAM started' }); }
    catch (e: any) { setAlertMessage({ type: 'error', message: e || 'Failed' }); }
  };

  const handleStopSlam = async () => {
    if (!selectedRobotId) return;
    try { await dispatch(stopSlam(selectedRobotId)).unwrap(); setSlamDialogOpen(true); }
    catch (e: any) { setAlertMessage({ type: 'error', message: e || 'Failed' }); }
  };

  const handleSaveSlamMap = async () => {
    if (!selectedRobotId || !slamMapName) return;
    try { await dispatch(saveSlamMap({ robotId: selectedRobotId, name: slamMapName })).unwrap(); setSlamDialogOpen(false); setSlamMapName(''); setAlertMessage({ type: 'success', message: 'Map save requested' }); }
    catch (e: any) { setAlertMessage({ type: 'error', message: e || 'Failed' }); }
  };

  const handleEditMapWaypoints = (mapId: string) => {
    navigate(`/maps/${mapId}/edit?robotId=${selectedRobotId}&tab=waypoints`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Map Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<Navigation />} 
            onClick={() => navigate(`/slam${selectedRobotId ? `?robot=${selectedRobotId}` : ''}`)}
          >
            SLAM Mapping
          </Button>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={handleDownloadAll}
            disabled={!selectedRobotId || bulkBusy || maps.length === 0}>Download All</Button>
          <Button variant="outlined" component="label" startIcon={<Upload />}
            disabled={!selectedRobotId || bulkBusy}>
            Import All
            <input hidden type="file" accept=".zip"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleUploadBulk(f); }} />
          </Button>
          <Button variant="contained" startIcon={<Upload />} onClick={() => setUploadDialogOpen(true)} disabled={!selectedRobotId}>Upload Map</Button>
        </Box>
      </Box>
      {alertMessage && <Alert severity={alertMessage.type} sx={{ mb: 3 }} onClose={() => setAlertMessage(null)}>{alertMessage.message}</Alert>}
      <Card sx={{ mb: 3 }}><CardContent>
        <TextField select label="Select Robot" value={selectedRobotId} onChange={(e) => setSelectedRobotId(e.target.value)} fullWidth>
          <MenuItem value=""><em>Select a robot</em></MenuItem>
          {robots.map((r) => <MenuItem key={r.id} value={r.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}><Typography>{r.serial_number}{r.friendly_name ? ` (${r.friendly_name})` : ''}</Typography><Box sx={{ flex: 1 }} /><Chip size="small" label={robotsOnline.includes(r.serial_number) ? 'Online' : 'Offline'} color={robotsOnline.includes(r.serial_number) ? 'success' : 'default'} /></Box></MenuItem>)}
        </TextField>
      </CardContent></Card>
      {!selectedRobotId ? <Card sx={{ p: 6, textAlign: 'center' }}><MapIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} /><Typography variant="h5" gutterBottom>Select a Robot</Typography></Card> :
        isLoading ? <Grid container spacing={3}>{[1, 2, 3].map((i) => <Grid item xs={12} md={6} lg={4} key={i}><Skeleton variant="rounded" height={200} /></Grid>)}</Grid> :
        maps.length === 0 ? <Card sx={{ p: 6, textAlign: 'center' }}><MapIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} /><Typography variant="h5" gutterBottom>No Maps Yet</Typography><Typography color="text.secondary" sx={{ mb: 2 }}>Create a map using SLAM or upload an existing one</Typography><Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}><Button variant="outlined" startIcon={<Navigation />} onClick={() => navigate(`/slam?robot=${selectedRobotId}`)}>Start SLAM Mapping</Button><Button variant="contained" startIcon={<Upload />} onClick={() => setUploadDialogOpen(true)}>Upload Map</Button></Box></Card> :
        <Grid container spacing={3}>{maps.map((map) => {
          const isLoaded = loadedMapId === map.id;
          return (
          <Grid item xs={12} md={6} lg={4} key={map.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: isLoaded ? '2px solid' : 'none', borderColor: 'success.main' }}>
              <Box sx={{ height: 160, bgcolor: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <MapIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
                {isLoaded && (
                  <Chip 
                    icon={<CheckCircle />} 
                    label="On Robot" 
                    color="success" 
                    size="small" 
                    sx={{ position: 'absolute', top: 8, right: 8 }} 
                  />
                )}
              </Box>
              <CardContent sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>{map.name}</Typography>
                <Typography variant="body2" color="text.secondary">Resolution: {map.resolution?.toFixed(3) || '--'} m/pixel</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Created: {new Date(map.created_at).toLocaleDateString()}</Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}>
                <Button 
                  size="small" 
                  variant={isLoaded ? 'outlined' : 'contained'}
                  startIcon={isLoaded ? <CheckCircle /> : (mapLoading ? <CircularProgress size={14} /> : <CloudDownload />)} 
                  onClick={() => handleSaveToRobot(map.id)} 
                  disabled={!isRobotOnline || !!mapLoading} 
                  color={isLoaded ? 'success' : 'primary'}
                >
                  {isLoaded ? 'Saved on Robot' : (mapLoading ? 'Saving...' : 'Save to Robot')}
                </Button>
                {isLoaded && (
                  <Button 
                    size="small" 
                    startIcon={<Refresh />} 
                    onClick={() => handleSaveToRobot(map.id)} 
                    disabled={!isRobotOnline || !!mapLoading}
                    color="info"
                    variant="outlined"
                  >
                    Re-save
                  </Button>
                )}
                <IconButton size="small" onClick={() => navigate(`/maps/${map.id}/edit?robotId=${selectedRobotId}`)}><Edit fontSize="small" /></IconButton>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" title="Download map (.zip)" onClick={() => handleDownload(map.id, map.name)}><FileDownload fontSize="small" /></IconButton>
                <IconButton size="small" title="Version history" onClick={() => openVersions(map)}><History fontSize="small" /></IconButton>
                <IconButton size="small" color="error" onClick={() => handleDelete(map.id)} disabled={isLoaded}><Delete fontSize="small" /></IconButton>
              </CardActions>
            </Card>
          </Grid>
        )})}</Grid>}
      {/* v6.3.50: map version history */}
      <Dialog open={!!versionsFor} onClose={() => setVersionsFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Version history — {versionsFor?.name}</DialogTitle>
        <DialogContent dividers>
          {versionsBusy && <CircularProgress size={22} />}
          {!versionsBusy && versions.length === 0 && (
            <Alert severity="info">
              No previous versions yet. A copy is archived automatically each
              time the robot re-uploads this map, keeping the last 5.
            </Alert>
          )}
          {!versionsBusy && versions.map((v: any) => (
            <Box key={v.version} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5,
                                       borderBottom: '1px solid rgba(128,128,128,0.15)' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>Version {v.version}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {v.archived_at ? new Date(v.archived_at).toLocaleString() : '—'} · {v.reason}
                  {v.actor ? ` · ${v.actor}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(v.files || []).length} files · {Math.round((v.size_bytes || 0) / 1024)} KB
                </Typography>
              </Box>
              <Button size="small" variant="outlined" startIcon={<RestoreFromTrash />}
                disabled={versionsBusy} onClick={() => restoreVersion(v.version)}>
                Restore
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionsFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Map</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Map Name" value={uploadForm.name} onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })} fullWidth required />

            {/* v6.3.36: zip is the default — it round-trips the archive produced
                by the Download button, which previously could not be re-uploaded. */}
            <ToggleButtonGroup size="small" exclusive value={uploadMode}
              onChange={(_, v) => v && setUploadMode(v)} sx={{ alignSelf: 'flex-start' }}>
              <ToggleButton value="zip">From .zip</ToggleButton>
              <ToggleButton value="files">Individual files</ToggleButton>
            </ToggleButtonGroup>

            {uploadMode === 'zip' ? (
              <>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Map Archive (.zip) *</Typography>
                  <input type="file" accept=".zip" onChange={(e) => setZipFile(e.target.files?.[0] || null)} />
                </Box>
                <Alert severity="info">
                  Use the .zip from a map's Download button. It must contain
                  map.pgm and map.yaml; keepout and waypoint files are picked up
                  automatically if present.
                </Alert>
              </>
            ) : (
            <>
            <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Required Files</Typography>
            <Box><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Map PGM File *</Typography><input type="file" accept=".pgm" onChange={(e) => setUploadForm({ ...uploadForm, pgmFile: e.target.files?.[0] || null })} /></Box>
            <Box><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Map YAML File *</Typography><input type="file" accept=".yaml,.yml" onChange={(e) => setUploadForm({ ...uploadForm, yamlFile: e.target.files?.[0] || null })} /></Box>
            
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Optional Files</Typography>
            
            <Box><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Keepout Mask PGM (optional)</Typography><input type="file" accept=".pgm" onChange={(e) => setUploadForm({ ...uploadForm, keepoutPgmFile: e.target.files?.[0] || null })} /></Box>
            <Box><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Keepout Mask YAML (optional)</Typography><input type="file" accept=".yaml,.yml" onChange={(e) => setUploadForm({ ...uploadForm, keepoutYamlFile: e.target.files?.[0] || null })} /></Box>
            <Box><Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Waypoints YAML (optional)</Typography><input type="file" accept=".yaml,.yml" onChange={(e) => setUploadForm({ ...uploadForm, waypointsFile: e.target.files?.[0] || null })} /></Box>
            
            <Alert severity="info" sx={{ mt: 1 }}>
              Keepout and waypoints can also be added later using the Map Editor.
            </Alert>
            </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button variant="contained"
            onClick={uploadMode === 'zip' ? handleUploadZip : handleUpload}
            disabled={uploadMode === 'zip'
              ? (!uploadForm.name || !zipFile)
              : (!uploadForm.name || !uploadForm.pgmFile || !uploadForm.yamlFile)}>Upload</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={slamDialogOpen} onClose={() => setSlamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save SLAM Map</DialogTitle>
        <DialogContent><Box sx={{ pt: 1 }}><TextField label="Map Name" value={slamMapName} onChange={(e) => setSlamMapName(e.target.value)} fullWidth required /></Box></DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setSlamDialogOpen(false)}>Cancel</Button><Button variant="contained" startIcon={<Save />} onClick={handleSaveSlamMap} disabled={!slamMapName}>Save Map</Button></DialogActions>
      </Dialog>
      
      {/* Map Load Error Dialog - Shows when waypoints are missing */}
      <Dialog open={!!mapLoadError} onClose={() => setMapLoadError(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Map Save Error
        </DialogTitle>
        <DialogContent>
          {mapLoadError?.error_type === 'waypoints_missing' ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This map requires waypoints before it can be loaded on the robot.
              </Alert>
              <Typography variant="body1" gutterBottom>
                <strong>Required waypoints:</strong>
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                {mapLoadError.required_waypoints?.map((wp) => (
                  <Typography key={wp} variant="body2" color="text.secondary">
                    • <strong>{wp}</strong> - {wp === 'home' ? 'Robot home/start position' : 'Charging dock position'}
                  </Typography>
                ))}
              </Box>
              {mapLoadError.current_waypoints && mapLoadError.current_waypoints.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Current waypoints: {mapLoadError.current_waypoints.join(', ')}
                  </Typography>
                </>
              )}
              <Typography variant="body2" sx={{ mt: 2 }}>
                Please open the Map Editor and add the required waypoints.
              </Typography>
            </>
          ) : (
            <Alert severity="error">{mapLoadError?.error}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMapLoadError(null)}>Close</Button>
          {mapLoadError?.error_type === 'waypoints_missing' && mapLoadError?.map_id && (
            <Button 
              variant="contained" 
              onClick={() => {
                setMapLoadError(null);
                handleEditMapWaypoints(mapLoadError.map_id);
              }}
            >
              Open Map Editor
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Map Loading Overlay */}
      {mapLoading && (
        <Dialog open={true} PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="white">
              {mapLoading === 'validating' && 'Validating map...'}
              {mapLoading === 'downloading' && 'Downloading map files...'}
              {mapLoading === 'starting' && 'Starting navigation...'}
              {mapLoading === 'requesting' && 'Sending request...'}
            </Typography>
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default MapManagement;
